import * as React from "react";
import * as moment from "moment";
import {isEmpty as _isEmpty} from "lodash"
import DatePicker from "react-datepicker";
import TextAreaAutoResize from "../../TextAreaAutoResize";
import { ILastMenstrualPeriod, ILMPKeyValues } from "@curefit/ups-common";
import TextInput from "../../TextInput";
import AppUtils from "../../../utils/AppUtils";
import IStaticDataValue from "../../../../../server/models/UIModels/commons/IStaticDataValue";
import CustomSelect from "../../CustomSelect";
import {AppConstants} from "../../../utils/AppConstants";

interface LMPEditorProps {
    lmpInfo: ILastMenstrualPeriod,
    saveHandler: (data: ILastMenstrualPeriod) => void,
    startDate: any
}

const periodicityDropDownValues: IStaticDataValue[] = [
    {displayValue: "Regular", internalValue: "REGULAR"},
    {displayValue: "Irregular", internalValue: "IRREGULAR"}
];
export default class LMPEditor extends React.Component<LMPEditorProps> {

    handleCommentOnChange = (e: React.SyntheticEvent<HTMLTextAreaElement>, key: ILMPKeyValues) => {
        let info: ILastMenstrualPeriod = { ...this.props.lmpInfo };
        if (_isEmpty(info[key])) info[key] = {};
        info[key].comment = (e.target as HTMLTextAreaElement).value;
        this.props.saveHandler(info);
    };

    handleDateChange = (date?: any) => {
        let info: ILastMenstrualPeriod = { ...this.props.lmpInfo };
        info["startDate"] = AppUtils.isDefined(date) ? moment(date).utc().format() : null;
        this.props.saveHandler(info);
    };

    handlePeriodicityValue = (item: string) => {
        const key: ILMPKeyValues = "periodicity";
        let info: ILastMenstrualPeriod = { ...this.props.lmpInfo };
        if (_isEmpty(info[key])) info[key] = {};
        info[key].value = item;
        this.props.saveHandler(info);
    };

    handleValueOnChange = (e: React.ChangeEvent<HTMLInputElement>, key: ILMPKeyValues) => {
        let info: ILastMenstrualPeriod = { ...this.props.lmpInfo };
        if (_isEmpty(info[key])) info[key] = {};
        const val = e.target.value;
        if (key === "pads") {
            info[key].value = AppUtils.isDefined(val) && /^\d*$/.test(val) ? val : "";
        } else {
            info[key].value = e.target.value;
        }
        this.props.saveHandler(info);
    };

    handleObstetricChange = (e: React.ChangeEvent<HTMLInputElement>, key: string) => {
        let info: ILastMenstrualPeriod = { ...this.props.lmpInfo };
        let obstetricHistory = info["obstetricHistory"];
        const val = e.target.value;
        if (_isEmpty(obstetricHistory)) obstetricHistory = {};
        obstetricHistory[key] = AppUtils.isDefined(val) && /^\d*$/.test(val) ? val : "";
        info = {...info, obstetricHistory};
        this.props.saveHandler(info);
    };

    handleDateOnBlur = (e: any) => {
        const date: string = e.target.value;
        if (moment(date, AppConstants.reverseDateFormat).isValid()) return;
        else {
            this.handleDateChange();
        }
    };

    render () {
        const hasLMPInfo = !_isEmpty(this.props.lmpInfo);
        const startDate = hasLMPInfo && AppUtils.isDefined(this.props.lmpInfo.startDate) ? moment(this.props.lmpInfo.startDate) : "";
        const durationComment = hasLMPInfo && !_isEmpty(this.props.lmpInfo.duration) && !_isEmpty(this.props.lmpInfo.duration.comment) ? this.props.lmpInfo.duration.comment : "";
        const cycleComment = hasLMPInfo && !_isEmpty(this.props.lmpInfo.cycle) && !_isEmpty(this.props.lmpInfo.cycle.comment) ? this.props.lmpInfo.cycle.comment : "";
        const padsComment = hasLMPInfo && !_isEmpty(this.props.lmpInfo.pads) && !_isEmpty(this.props.lmpInfo.pads.comment) ? this.props.lmpInfo.pads.comment : "";
        const periodicityComment = hasLMPInfo && !_isEmpty(this.props.lmpInfo.periodicity) && !_isEmpty(this.props.lmpInfo.periodicity.comment) ? this.props.lmpInfo.periodicity.comment : "";
        const obstetricHistoryComment = hasLMPInfo && !_isEmpty(this.props.lmpInfo.obstetricHistory) && !_isEmpty(this.props.lmpInfo.obstetricHistory.comment) ? this.props.lmpInfo.obstetricHistory.comment : "";
        const selectedPeriodicityValue = hasLMPInfo && !_isEmpty(this.props.lmpInfo.periodicity) && !_isEmpty(this.props.lmpInfo.periodicity.value) ? this.props.lmpInfo.periodicity.value : null;
        const durationValue = hasLMPInfo && !_isEmpty(this.props.lmpInfo.duration) && AppUtils.isDefined(this.props.lmpInfo.duration.value) ? this.props.lmpInfo.duration.value : null;
        const cycleValue = hasLMPInfo && !_isEmpty(this.props.lmpInfo.cycle) && AppUtils.isDefined(this.props.lmpInfo.cycle.value) ? this.props.lmpInfo.cycle.value : null;
        const padsValue = hasLMPInfo && !_isEmpty(this.props.lmpInfo.pads) && AppUtils.isDefined(this.props.lmpInfo.pads.value) ? this.props.lmpInfo.pads.value : null;
        const hasObstetricHistory = hasLMPInfo && !_isEmpty(this.props.lmpInfo.obstetricHistory);
        const gravidaValue = hasObstetricHistory && AppUtils.isDefined(this.props.lmpInfo.obstetricHistory.gravida) ? this.props.lmpInfo.obstetricHistory.gravida : null;
        const paraValue = hasObstetricHistory && AppUtils.isDefined(this.props.lmpInfo.obstetricHistory.para) ? this.props.lmpInfo.obstetricHistory.para : null;
        const livingValue = hasObstetricHistory && AppUtils.isDefined(this.props.lmpInfo.obstetricHistory.living) ? this.props.lmpInfo.obstetricHistory.living : null;
        const abortusValue = hasObstetricHistory && AppUtils.isDefined(this.props.lmpInfo.obstetricHistory.abortus) ? this.props.lmpInfo.obstetricHistory.abortus : null;
        return (
            <div className={"lmp-editor"}>
                <div className={"questionnaire-component"}>
                    <div className={"question-text"}>Start Date</div>
                    <div className={"input-wrapper"}>
                        <DatePicker
                            placeholderText={"Click to select a date"}
                            selected={startDate}
                            onChange={this.handleDateChange}
                            dateFormat={AppConstants.reverseDateFormat}
                            onBlur={this.handleDateOnBlur}
                        />
                    </div>
                </div>
                <div className={"questionnaire-component"}>
                    <div className={"question-text"}>Duration</div>
                    <div className={"input-wrapper"}>
                        <TextInput name={"duration"} placeholder={"duration"} value={durationValue} onChange={e => this.handleValueOnChange(e, "duration")}/>
                    </div>
                    <div>
                        <TextAreaAutoResize
                            name={"lmp-duration-comment"}
                            className={"textarea-input form-control"}
                            rows={2}
                            placeholder={"Add Comment"}
                            value={durationComment}
                            onChange={(e: React.SyntheticEvent<HTMLTextAreaElement>) => this.handleCommentOnChange(e, "duration")}/>
                    </div>
                </div>
                <div className={"questionnaire-component"}>
                    <div className={"question-text"}>Cycle</div>
                    <div className={"input-wrapper"}>
                        <TextInput name={"cycle"} placeholder={"cycle"} value={cycleValue} onChange={e => this.handleValueOnChange(e, "cycle")}/>
                    </div>
                    <div>
                        <TextAreaAutoResize
                            name={"lmp-cycle-comment"}
                            className={"textarea-input form-control"}
                            rows={2}
                            placeholder={"Add Comment"}
                            value={cycleComment}
                            onChange={(e: React.SyntheticEvent<HTMLTextAreaElement>) => this.handleCommentOnChange(e, "cycle")}/>
                    </div>
                </div>
                <div className={"questionnaire-component"}>
                    <div className={"question-text"}>Pads</div>
                    <div className={"input-wrapper"}>
                        <TextInput name={"pads"} placeholder={"no. of pads"} value={padsValue} onChange={e => this.handleValueOnChange(e, "pads")}/>
                    </div>
                    <div>
                        <TextAreaAutoResize
                            name={"lmp-pads-comment"}
                            className={"textarea-input form-control"}
                            rows={2}
                            placeholder={"Add Comment"}
                            value={padsComment}
                            onChange={(e: React.SyntheticEvent<HTMLTextAreaElement>) => this.handleCommentOnChange(e, "pads")}/>
                    </div>
                </div>
                <div className={"questionnaire-component"}>
                    <div className={"question-text"}>Periodicity</div>
                    <div className={"input-wrapper"}>
                        <CustomSelect dropdownValues={periodicityDropDownValues} selectedValue={selectedPeriodicityValue} defaultValue={null} updateDropdownValue={this.handlePeriodicityValue}/>
                    </div>
                    <div>
                        <TextAreaAutoResize
                            name="lmp-periodicity-comment"
                            className={"textarea-input form-control"}
                            rows={2}
                            placeholder={"Add Comment"}
                            value={periodicityComment}
                            onChange={(e: React.SyntheticEvent<HTMLTextAreaElement>) => this.handleCommentOnChange(e, "periodicity")}/>
                    </div>
                </div>
                <div className={"questionnaire-component"}>
                    <div className={"question-text"}>Obstetric History</div>
                    <div className={"input-wrapper"}>
                        <div className={"questionnaire-component"}>
                            <div className={"question-text"}>Gravida</div>
                            <TextInput name={"gravida"} placeholder={"value"} value={gravidaValue} onChange={e => this.handleObstetricChange(e, "gravida")}/>
                        </div>
                        <div className={"questionnaire-component"}>
                            <div className={"question-text"}>Para</div>
                            <TextInput name={"para"} placeholder={"value"} value={paraValue} onChange={e => this.handleObstetricChange(e, "para")}/>
                        </div>
                        <div className={"questionnaire-component"}>
                            <div className={"question-text"}>Living</div>
                            <TextInput name={"living"} placeholder={"value"} value={livingValue} onChange={e => this.handleObstetricChange(e, "living")}/>
                        </div>
                        <div className={"questionnaire-component"}>
                            <div className={"question-text"}>Abortus</div>
                            <TextInput name={"abortus"} placeholder={"value"} value={abortusValue} onChange={e => this.handleObstetricChange(e, "abortus")}/>
                        </div>
                    </div>
                    <div>
                        <TextAreaAutoResize
                            name="lmp-obstetric-history-comments"
                            className={"textarea-input form-control"}
                            rows={2}
                            placeholder={"Add Comment"}
                            value={obstetricHistoryComment}
                            onChange={(e: React.SyntheticEvent<HTMLTextAreaElement>) => this.handleCommentOnChange(e, "obstetricHistory")}/>
                    </div>
                </div>
            </div>
        )
    }
}

